import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AuthResponse } from '@conversia/shared-types';
import { api } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import { Spinner } from '@/components/ui/Spinner';

/**
 * ENTRAR DESDE EL GLOBAL (apps/api/src/services/pase-global.service.ts).
 *
 * El Conversia global abre esta página con un pase en el fragmento (#pase=…). Se canjea UNA vez por una
 * sesión del dueño de este Conversia y se borra de la barra de direcciones en el acto: no queda en el
 * historial ni se puede reusar. Si no vale, se dice y se ofrece entrar con la clave.
 */
export default function EntrarGlobal() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  const hecho = useRef(false);   // en desarrollo React monta dos veces: el pase se gasta una sola

  useEffect(() => {
    if (hecho.current) return;
    hecho.current = true;
    const pase = new URLSearchParams(window.location.hash.slice(1)).get('pase');
    window.history.replaceState(null, '', '/entrar-global');
    if (!pase) {
      setError('Aquí se llega desde el Conversia global, con el botón «Entrar».');
      return;
    }
    api
      .post<AuthResponse>('/api/auth/pase', { pase }, { skipAuth: true })
      .then((sesion) => {
        setSession(sesion);
        navigate('/', { replace: true });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'No se pudo entrar con el pase.'));
  }, [navigate, setSession]);

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-muted p-6 dark:bg-slate-950">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-surface-border bg-white p-6 text-center dark:border-slate-800 dark:bg-slate-900">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No se pudo entrar</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
            <Link to="/login" className="inline-block text-sm font-medium text-brand-600 hover:underline">
              Entrar con mi clave
            </Link>
          </>
        ) : (
          <>
            <Spinner size={28} className="mx-auto text-brand-600" />
            <p className="text-sm text-slate-600 dark:text-slate-300">Entrando desde el Conversia global…</p>
          </>
        )}
      </div>
    </div>
  );
}
